import { createSelector } from 'reselect';
import { EXPIRED_STATUS } from 'resources/ConsumerLending/FinancingDetails/Data';
import { isBlank } from 'utils/isBlank';
import { isPresent } from 'utils/isPresent';

import { receivedServerData } from '../../Common/Data/DataFetcher';
import { DebitTransaction, JobDetailsState, PaymentInformation } from '../Types/JobAndEstimateDetails';

export const JOB_NAMESPACE = 'job';
export const SCHEDULE_INVOICE_PAYMENT_EXPERIMENT = 'schedule-invoice-payment';

export const getJob = createSelector(
  (state: { job: JobDetailsState }) => state.job,
  (job) => {
    return job || {};
  }
);

export const getBasicInfo = createSelector(getJob, (job) => {
  return job.basic_info || {};
});

export const getJobId = createSelector(getBasicInfo, (basicInfo) => {
  return basicInfo.id || '';
});

export const getJobUuid = createSelector(getBasicInfo, (basicInfo) => {
  return basicInfo.uuid || '';
});

export const getIsEstimate = createSelector(getBasicInfo, (basicInfo): boolean => {
  return basicInfo.is_estimate || false;
});

export const getEstimateExpirationDate = createSelector(getBasicInfo, (basicInfo) => {
  return basicInfo.expiration_date || null;
});

export const getRequestLabel = createSelector(getIsEstimate, (isEstimate) => {
  return isEstimate ? 'estimate' : 'job';
});

export const getSegmentLabel = createSelector(getIsEstimate, (isEstimate) => {
  return isEstimate ? 'option' : 'segment';
});

export const getInvoiceOptions = createSelector(
  (state: {
    invoiceOptions: {
      allow_pay_by_cc: unknown;
      allow_pay_by_ach: unknown;
      allow_card_on_file: unknown;
      is_payout_method_standard: unknown;
    };
  }) => state.invoiceOptions,
  (invoiceOptions) => {
    return invoiceOptions || {};
  }
);

export const getPaymentInformation = createSelector(getJob, (job) => {
  return job.payment_information || {};
});

export const getDebitTransactions = createSelector(getPaymentInformation, (paymentInfo) => {
  return paymentInfo.debit_transactions || [];
});

export const getEstimateStatus = createSelector(getPaymentInformation, (paymentInfo) => {
  return paymentInfo.estimate_status || '';
});

export const hasEstimateExpiredStatus = createSelector(getEstimateStatus, (estimateStatus) => {
  return estimateStatus === EXPIRED_STATUS;
});

export const getRemainingDeposit = createSelector(getPaymentInformation, (paymentInfo) => {
  return paymentInfo.remaining_deposit || 0;
});

export const getSegments = createSelector(getJob, (job) => {
  return job.segments || {};
});

export const getOverviewInvoiceDue = createSelector(getSegments, (segments) => {
  if (isBlank(segments?.segments?.[0])) return null;
  return segments.segments[0].invoice_due || null;
});

export const getInvoiceDue = createSelector(getOverviewInvoiceDue, (overviewInvoiceDue) => {
  if (isBlank(overviewInvoiceDue)) return null;
  const invoiceDue = overviewInvoiceDue.toLowerCase();
  if (invoiceDue.includes('net') || invoiceDue.includes('upon')) {
    return invoiceDue;
  }
  return 'custom date';
});

export const getDepositAmount = createSelector(getPaymentInformation, (paymentInfo) => {
  return paymentInfo.deposit_amount || 0;
});

export const getDepositDueDate = createSelector(getPaymentInformation, (paymentInfo) => {
  return paymentInfo.deposit_due_date || '';
});

export const isDepositPaid = createSelector(
  [getDepositAmount, getRemainingDeposit],
  (depositAmount, remainingDeposit) => {
    return depositAmount > 0 && remainingDeposit === 0;
  }
);

export const getSuccessfulTransactions = createSelector(getDebitTransactions, (debitTransactions) => {
  return debitTransactions.filter(
    (transaction) => transaction.kind.indexOf('failed') === -1 && transaction.kind.indexOf('pending') === -1
  );
});

export const getDepositPaidDate = createSelector(
  [isDepositPaid, getDepositAmount, getSuccessfulTransactions],
  (isDepositPaidValue, depositAmount, successfulTransactions) => {
    return isDepositPaidValue ? getMostRecentDepositPaidDate(successfulTransactions, depositAmount) : '';
  }
);

export const getAllowCC = createSelector(getInvoiceOptions, (invoiceOptions) => {
  return invoiceOptions.allow_pay_by_cc || false;
});

export const getAllowACH = createSelector(getInvoiceOptions, (invoiceOptions) => {
  return invoiceOptions.allow_pay_by_ach || false;
});

export const getAllowCardOnFile = createSelector(getInvoiceOptions, (invoiceOptions) => {
  return invoiceOptions.allow_card_on_file || false;
});

export const getDepositSpeed = createSelector(getInvoiceOptions, (invoiceOptions) => {
  return invoiceOptions.is_payout_method_standard ? 'standard' : 'instapay';
});

export const getOverviewAmountDue = createSelector(getSegments, (segments) => {
  return segments.overview_total_outstanding_balance / 100.0;
});

export const getOverviewInvoiceNumber = createSelector(getSegments, (segments) => {
  return segments.overview_invoice_number;
});

export const getCustomerFromTheJob = createSelector(getJob, (job) => {
  return job.customer;
});

export const getCustomerIdFromTheJob = createSelector(getCustomerFromTheJob, (customer) => {
  return customer.customer_id;
});

export const getServiceItems = createSelector(getJob, (job) => {
  return job.service_items || [];
});

export const getJobInboxFromJob = createSelector(getJob, (job) => {
  return job.job_inbox || ({} as Record<string, never>);
});

export const jobIsJobInbox = createSelector(getJobInboxFromJob, (jobInbox) => {
  return isPresent(jobInbox);
});

export const getPartnerJobUuid = createSelector(getJobInboxFromJob, (job_inbox) => {
  return job_inbox?.uuid;
});

export const getParnerFromJobInbox = createSelector(getJobInboxFromJob, (job_inbox) => {
  return job_inbox?.partner || {};
});

export const getPartnerName = createSelector(getParnerFromJobInbox, (partner) => {
  return partner.name;
});

export function receivedServiceItems(service_items: unknown[]) {
  return receivedServerData({ service_items }, JOB_NAMESPACE);
}

export const getSearchableServices = createSelector(getJob, (job) => {
  return job.searchable_services || [];
});

export const getSearchableMaterials = createSelector(getJob, (job) => {
  return job.searchable_materials || [];
});

export function receivedSearchableService(services: unknown) {
  return receivedServerData({ searchable_services: services }, JOB_NAMESPACE);
}

export function receivedSearchableMaterial(materials: unknown) {
  return receivedServerData({ searchable_materials: materials }, JOB_NAMESPACE);
}

export function receivedSearchableMaterialService(materials: unknown, services: unknown) {
  return receivedServerData({ searchable_materials: materials, searchable_services: services }, JOB_NAMESPACE);
}

export function getTransactionAmount(transaction: DebitTransaction) {
  return transaction.kind.indexOf('refund') === -1 ? transaction.amount : -1 * transaction.amount;
}

export function getMostRecentDepositPaidDate(
  transactions: PaymentInformation['debit_transactions'],
  depositAmount: number
) {
  let paidAmount = 0;
  let depositPaidDate = '';
  let updatePaidDate = true;
  transactions.forEach((transaction) => {
    paidAmount += getTransactionAmount(transaction);
    if (paidAmount >= depositAmount) {
      if (updatePaidDate) {
        depositPaidDate = transaction.when;
        updatePaidDate = false;
      }
    } else {
      updatePaidDate = true;
    }
  });
  return depositPaidDate;
}

export const getEditAddressDialogState = createSelector(
  (state: { editAddressDialogOpen: unknown }) => state.editAddressDialogOpen,
  (editAddressDialogOpen) => {
    return editAddressDialogOpen || false;
  }
);
